import React, { useState } from "react";
import { useSearchParams } from "react-router-dom";
import { HiOutlineCheckCircle } from "react-icons/hi2";
import { appConfig } from "../../app-config";
import { APP_ROUTES } from "../../data-access";
import { FormButton } from "../../ui";
import "./uninstall-feedback.scss";

// Exit-опитування при видаленні розширення. Публічний роут БЕЗ guard -
// юзер уже розлогінений/видалив розширення. Email приходить у query
// (?email=... - розширення памʼятає останній логін), якщо його немає -
// показуємо поле вводу.

const REASONS: { value: string; label: string }[] = [
  { value: "hard_to_use", label: "I couldn't figure out how to use it" },
  { value: "no_results", label: "It didn't help me win more jobs" },
  { value: "too_expensive", label: "Too expensive for what I got" },
  { value: "competitor", label: "I found a tool I like better" },
  { value: "just_trying", label: "I only installed it to try it out" },
  { value: "buggy_slow", label: "It was buggy or slow" },
  { value: "other", label: "Something else" },
];

export const UninstallFeedback = (): JSX.Element => {
  const [searchParams] = useSearchParams();
  const emailFromQuery = (searchParams.get("email") || "").trim();
  const extensionVersion = searchParams.get("v") || null;

  const [reason, setReason] = useState<string | null>(null);
  const [otherReason, setOtherReason] = useState("");
  const [comment, setComment] = useState("");
  const [email, setEmail] = useState(emailFromQuery);
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);

  const handleSend = () => {
    if (!reason || sending) return;
    setSending(true);

    const payload = {
      reason,
      other_reason: reason === "other" ? otherReason.trim() || null : null,
      comment: comment.trim() || null,
      email: email.trim() || null,
      extension_version: extensionVersion,
      uninstalled_at: new Date().toISOString(),
    };

    // Фідбек не блокує юзера: подяку показуємо незалежно від результату
    // (keepalive - щоб запит долетів, навіть якщо вкладку одразу закриють).
    // Без Authorization - юзер видалив розширення, токена може не бути.
    try {
      fetch(`${process.env.REACT_APP_API_ENDPOINT}/api/feedback/uninstall`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
        keepalive: true,
      }).catch(() => {});
    } catch {
      /* noop */
    }

    setSent(true);
  };

  if (sent) {
    return (
      <div className="uninstall-page">
        <div className="uninstall-card uninstall-card--thanks">
          <span className="uninstall-check">
            <HiOutlineCheckCircle size={34} />
          </span>
          <h1>Thank you, this helps.</h1>
          <p className="uninstall-sub">
            We read every response. If Upwex ever earns a second look, you're
            welcome back anytime.
          </p>
          <div className="uninstall-actions">
            <FormButton
              theme="grey"
              className="uninstall-btn"
              onClick={() => {
                window.location.href = APP_ROUTES.login;
              }}
            >
              Go to login
            </FormButton>
            <FormButton
              theme="green-light"
              className="uninstall-btn"
              onClick={() =>
                window.open(
                  appConfig.extensionDownloadLink,
                  "_blank",
                  "noopener,noreferrer"
                )
              }
            >
              Reinstall Upwex
            </FormButton>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="uninstall-page">
      <div className="uninstall-card">
        <h1>Before you go, one quick question</h1>
        <p className="uninstall-sub">
          You just removed Upwex. Telling us why takes 10 seconds and genuinely
          shapes what we fix next.
        </p>

        <div className="uninstall-q">What made you uninstall?</div>
        <div className="uninstall-options">
          {REASONS.map((r) => (
            <label
              key={r.value}
              className={`uninstall-opt ${reason === r.value ? "is-selected" : ""}`}
            >
              <input
                type="radio"
                name="reason"
                value={r.value}
                checked={reason === r.value}
                onChange={() => setReason(r.value)}
              />
              <span className="uninstall-dot" />
              {r.label}
            </label>
          ))}
        </div>

        {reason === "other" && (
          <>
            <label className="uninstall-label" htmlFor="uninstall-other">
              What was it?
            </label>
            <input
              id="uninstall-other"
              className="uninstall-input"
              type="text"
              maxLength={300}
              placeholder="A few words about your reason..."
              value={otherReason}
              onChange={(e) => setOtherReason(e.target.value)}
            />
          </>
        )}

        {!emailFromQuery && (
          <>
            <label className="uninstall-label" htmlFor="uninstall-email">
              Your email (optional)
            </label>
            <input
              id="uninstall-email"
              className="uninstall-input"
              type="email"
              placeholder="you@example.com"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
            />
          </>
        )}

        <label className="uninstall-label" htmlFor="uninstall-comment">
          Anything specific we should know? (optional)
        </label>
        <textarea
          id="uninstall-comment"
          className="uninstall-textarea"
          maxLength={2000}
          placeholder="Your words go straight to the team..."
          value={comment}
          onChange={(e) => setComment(e.target.value)}
        />

        <FormButton
          theme="green-light"
          className="uninstall-btn"
          disabled={!reason || sending}
          onClick={handleSend}
        >
          {sending ? "Sending..." : "Send feedback"}
        </FormButton>
      </div>
    </div>
  );
};

export default UninstallFeedback;
